import { fmtDuration, fmtRowCount, fmtWhen } from './queueFormat'
import { InfoIcon } from './queueIcons'
import { jobTrigger } from './jobIdentity'
import { expiredSentence } from './resultRetention'

/**
 * "today 2:59 PM" / "yesterday 4:02 PM" / "Oct 1, 12:30 PM" — `fmtWhen`, mid-sentence.
 *
 * Spec 167: this line used to lower-case ALL of `fmtWhen`'s output so "Today" would sit
 * mid-sentence, which also printed "Ran oct 1, 12:30 pm" for every run older than
 * yesterday — a month with no capital and a lower-case meridiem, beside a drawer whose
 * every other time reads "12:30 PM" (seen live on QA run 410cf866, 2026-10-12). Only the
 * two relative words are sentence-case words; the month and the meridiem keep the shape
 * `fmtWhen` gives them everywhere else.
 */
const ranWhen = (ms) => fmtWhen(ms).replace(/^(Today|Yesterday)\b/, (w) => w.toLowerCase())

/**
 * Screen 8C — an expired result.
 *
 * The run DETAILS survive (they live in D1); only the stored payload is gone
 * (the R2 results bucket). Saying so plainly is the whole point of this state —
 * otherwise an expired row looks like a broken one.
 *
 * Retention copy is split, and the split is a correctness one. THREE clocks bound a
 * stored result, and `resultRetention.js` (spec 152) is the one place that words them:
 *
 *   1. the ad-hoc row's TTL — `expires_at`, one week (`RESULT_TTL_HOURS`);
 *   2. a schedule's count — "keep the last N runs", enforced the moment each run
 *      FINISHES (spec 152 F2c), which deletes the older runs outright, row and all;
 *   3. the bucket's age — its lifecycle rule deletes every result OBJECT by age (7 days
 *      on QA, read from the rule), whatever 1 or 2 say. cb-api folds it into
 *      `resultExpiresAt` on every job.
 *
 * So a schedule's run DOES expire on a clock (3) while its row is kept by count (2):
 * this panel is what such a run shows once 3 has passed and 2 has not. One flat
 * sentence is false for one of the populations.
 *
 * ⚠️ Superseded statements, kept so nobody restores them: this comment used to say the
 * payload had a "24h TTL" (it is one week), that a schedule's run has "NO time-based
 * expiry at all" (the bucket's age, 3, applies to it), and that it is pruned "when the
 * schedule next fires" (since spec 152, when each run finishes). The rendered copy read
 * the module, so only the comment was wrong — which is its own small lesson about
 * which of the two a reader trusts.
 *
 * ## Buttons moved (spec 111)
 *
 * `onRerun` / `onDismiss` and their two buttons moved to the drawer's fixed
 * footer, and the props were REMOVED rather than left accepted-and-ignored. The
 * footer is also where the re-run gate lives — re-running from the drawer is a
 * silent no-op on the 21 specialized reports, and this panel cannot know that.
 *
 * The scheduled/ad-hoc test now reads `scheduleId`, not `scheduleName`:
 * `scheduleName` is resolved client-side and is absent whenever that lookup has
 * not happened, so a schedule-produced run was being told the ad-hoc TTL applied
 * to it. `scheduleId` comes straight off the row.
 *
 * Spec 132 — and it asks `jobTrigger`, the one provenance rule, which can answer a
 * THIRD way: "Not recorded" (a payload with no schedule key at all). That run is
 * told neither retention rule, because either could be false for it; the neutral
 * sentence below is true whatever produced it. The same goes for a schedule run
 * whose schedule is provably deleted: it no longer "keeps its last results".
 * "Provably" is `nameJob`'s rule — a stale or truncated schedule list proves nothing,
 * and such a run keeps the schedule sentence.
 *
 * Spec 152 — the sentence comes from `resultRetention.expiredSentence`, the module that
 * owns every retention sentence, and names the results bucket's age when the API
 * declares it ("This schedule keeps its last 3 runs. Each run’s data can be opened for
 * up to 7 days; its details stay while it is one of the 3."). `gone`
 * is set by the drawer when the result route answered 410 for a run that still read
 * Ready: the object went before any date we had, so the panel says only that.
 */
const JobExpiredPanel = ({ job, gone = false }) => {
  if (!job) return null
  const trigger = jobTrigger(job)
  const sentence = expiredSentence({ job, trigger, gone })

  return (
    <div>
      <div data-testid="job-expired-ran" className="mb-3 text-[12.5px] text-gray-500">
        Ran {ranWhen(job.createdAt)} · {fmtRowCount(job.rowCount)} in {fmtDuration(job.durationMs)}
      </div>

      <div className="mb-3.5 flex gap-2.5 rounded-xl border border-cb-accent/30 bg-cb-light px-3 py-2.5 text-[11.8px] leading-relaxed text-cb-navy">
        <InfoIcon className="mt-px w-3.5 h-3.5 shrink-0" />
        <span data-testid="job-expired-sentence">{sentence}</span>
      </div>
    </div>
  )
}

export default JobExpiredPanel
